import { lazy } from 'react';
import { RouteObject } from 'react-router-dom';

import Error404 from '../error/Error404/Error404';
import ErrorBoundary from '../error/ErrorBoundary/ErrorBoundary';
import billUploadRoute from '../features/BillUpload/routes';
import campaignRoute from '../features/Campaign/routes';
import campaignListRoute from '../features/CampaignList/routes';
import socialMediaCampaignListRoute from '../features/SocialMediaCampaign/routes';
import casaFieldsRoute from '../features/CasaFields/routes';
import cohortAnalysisRoute from '../features/CohortAnalysis/routes';
import customerAnalysisRoute from '../features/CustomerAnalysis/routes';
import customerCubeSettingsRoute from '../features/CustomerCubeSettings/routes';
import productAnalysisRoute from '../features/ProductAnalysis/routes';
import productFrequencyRoute from '../features/ProductFrequency/routes';
import settingsRoute from '../features/Settings/routes';
import SchemeManagementRoute from '../features/SchemeManagement/routes';
import SchemeEnrollmentRoute from '../features/SchemeManagement/routes/enrollment';
import ExtendValidityRoute from '../features/SchemeManagement/routes/validity';
import storeAnalysisRoute from '../features/StoreAnalysis/routes';
import uploadDataRoutes from '../features/UploadData/routes';
import DashboardLayout from '../layouts/Dashboard';
import overviewDashboardRoute from '../features/OverviewDashboard/routes';
import overviewDashboardV2Route from '../features/OverviewDashboardV2/routes';
import festivalAnalysisRoute from '../features/FestivalAnalysis/routes';
import RetenseeRoute from '../features/RetenseeSetting/routes';
import ExclusionRoute from '../features/Exclusions/routes';
import LoyaltyMilestone from '../features/LoyaltyMilestone/routes';
import EarnMultiplierRoute from '../features/EarnMultiplier/routes';
import CopyWriteTemplateRoutes from '../features/CopyWriteTemplate/routes';
import customer360Route from '../features/Customer360/routes';
import roleConfigRoute from '../features/RoleManagement/routes';
import StoreManagementRoute from '../features/StoreManagement/routes';
import UserManagementRoute from '../features/UserManagement/routes';
import SurveyCreator, { SurveyFormRoute } from '../features/SurveyJs/routes';
import SurveyDashboardRoute from '../features/SurveyDashboard/routes';
import InvoiceCreatorRoute from '../features/InvoiceCreator/routes';
import InvoiceCreatorV2Route, { InvoiceV2Route } from '../features/InvoiceCreatorV2/routes';
import BrandThemeRoute from '../features/BrandTheme/routes';
import InvoiceRoute from '../features/InvoiceGenerator/routes';
import omniChannelDashboardRoute from '../features/OmniChannelDashboard/routes';
import BlacklistConfigRoute from '../features/BlacklistManagement/routes';
import LeadDashboardRoute from '../features/LeadDashboard/routes';
import storeStatusReport from '../features/StoreStatusReports/routes';
import customer360POS from '../features/Customer360/routes/pos-route';
import ssoEmbedLeadRoutes from '../features/ManageLead/routes/sso-embed-route';
import intellicloseConfigRoute from '../features/IntellicloseConfig/routes';
import leadConfigRoute from '../features/CreateLead/routes';
import ManageLeadRoute from '../features/ManageLead/routes';
import FlipSellRoute from '../features/FlipSell/Routes';
import journeyBuilderRoute from '../features/JourneyBuilder/routes';
import journeyBuilderSettingsRoute from '../features/JourneyBuilderSettings/routes';
import journeyListRoutes from '../features/JourneyList/routes';
import SurveySettingsRoute from '../features/SurveySettings/routes';
import PersonifyConfigRoute from '../features/Personify/routes';
import OneFlowOtpConfigRoute from '../features/OneFlowOtpConfig/routes';
import LoyaltyOtpConfigRoute from '../features/LoyaltyOtpConfig/routes';
import OfferOtpConfigRoute from '../features/OfferOtpConfig/routes';
import GiftVoucherOtpConfigRoute from '../features/GiftVoucherOtpConfig/routes';
import CommerceConnectSettingsRoute from '../features/CommerceConnect/Settings/routes';
import storePrediction from '../features/StorePrediction/routes';
import MufflerRoute from '../features/MufflerSettings/routes';
import integrationRoute from '../features/Integrations/routes';
import productAttributes from '../features/ProductAttribute/routes';
import UploadProducts from '../features/UploadProducts/routes';
import TaskDetail from '../features/TaskDetail/routes';
import telecallLogs from '../features/TelecallHistory/routes';
import ReportsRoute from '../features/Reports/routes';
import CustomerMigrateRoute from '../features/CustomerMigrate/routes';
import CampaignAnalysisRoute from '../features/CampaignAnalysis/routes';
import LoyaltyAdhocPointsRoute from '../features/LoyaltyAdhocPoints/routes';
import TemplateRoutes from '../features/Templates/routes';
import channelWiseBlacklist from '../features/ChannelWiseBlacklist/routes';
import SchedulerListingRoute from "../features/Scheduler/routes";
import ticketDesk from "../features/TicketDesk/routes";
import friendReferralSettingsRoute from "../features/FriendReferralSettings/routes";
import friendReferralOtpRoute from "../features/FriendReferralOtp/routes";
import cohortListRoutes from '../features/CohortList/routes';
import executiveSummaryRoute from '../features/ExecutiveSummary/routes';
import OfferConfigRoute from "../features/CreateOffer/routes";
import GiftVoucherConfigRoute from "../features/CreateGiftVoucher/routes";
import OfferListConfigRoute from "../features/OfferList/routes";
import CustomerOfferSearchRoute from "../features/CustomerOfferSearch/routes";
import GiftVoucherListConfigRoute from "../features/GiftVoucherList/routes";
import BasketAnalysisRoute from '../features/BasketAnalysis/routes';
import RaiseIssueInvoiceRoute from '../features/RaiseIssueInvoice/routes';
import EcomSettingsRoute from '../features/EcomSettings/routes';
import OverallJourneyAnalyticsRoutes from '../features/OverallJourneyAnalytics/routes';
import IndividualJourneyAnalyticsRoutes from '../features/IndividualJourneyAnalytics/routes';
import HealthRoutes from '../features/Health/routes';
import SystemRoutes from '../features/System/routes';
import JourneyRequestsRoutes, { DLTFormRoute } from '../features/JourneyRequests/routes';
import FeatureRequestsRoutes from '../features/FeatureRequests/routes';
import ProductPreviewRoutes from '../features/ProductPreview/routes';
import onlineBehaviourRoute from '../features/Ecom/OnlineBehaviour/routes';
import abandonmentIntelligenceRoute from '../features/Ecom/AbandonmentIntelligence/routes';
import adAttributionRoute from '../features/Ecom/AdAttribution/routes';
import multiTouchRoute from '../features/Ecom/MultiTouch/routes';

const PosDashboard = lazy(() => import('../layouts/Dashboard/components/PosDashboard'));
const SSOEmbedLayout = lazy(() => import('../layouts/SSOEmbed/SSOEmbed'));
const PageGuard = lazy(() => import('../guards/PageGuard'));

const routesPages: RouteObject[] = [
  onlineBehaviourRoute,
  abandonmentIntelligenceRoute,
  adAttributionRoute,
  multiTouchRoute,
  omniChannelDashboardRoute,
  productFrequencyRoute,
  uploadDataRoutes,
  storeAnalysisRoute,
  billUploadRoute,
  cohortAnalysisRoute,
  campaignRoute,
  campaignListRoute,
  socialMediaCampaignListRoute,
  ...roleConfigRoute,
  RetenseeRoute,
  SurveySettingsRoute,
  EcomSettingsRoute,
  CommerceConnectSettingsRoute,
  ExclusionRoute,
  LoyaltyMilestone,
  EarnMultiplierRoute,
  StoreManagementRoute,
  UserManagementRoute,
  LeadDashboardRoute,
  leadConfigRoute,
  ManageLeadRoute,
  StoreManagementRoute,
  customerCubeSettingsRoute,
  settingsRoute,
  SchemeManagementRoute,
  SchemeEnrollmentRoute,
  ExtendValidityRoute,
  productAnalysisRoute,
  customerAnalysisRoute,
  casaFieldsRoute,
  overviewDashboardRoute,
  overviewDashboardV2Route,
  festivalAnalysisRoute,
  CopyWriteTemplateRoutes,
  TemplateRoutes,
  customer360Route,
  InvoiceCreatorRoute,
  InvoiceCreatorV2Route,
  BrandThemeRoute,
  SurveyCreator,
  SurveyDashboardRoute,
  intellicloseConfigRoute,
  PersonifyConfigRoute,
  OneFlowOtpConfigRoute,
  LoyaltyOtpConfigRoute,
  OfferOtpConfigRoute,
  GiftVoucherOtpConfigRoute,
  ReportsRoute,
  LoyaltyAdhocPointsRoute,
  storePrediction,
  storeStatusReport,
  FlipSellRoute,
  ...journeyBuilderRoute,
  journeyListRoutes,
  cohortListRoutes,
  OfferConfigRoute,
  GiftVoucherConfigRoute,
  OfferListConfigRoute,
  CustomerOfferSearchRoute,
  GiftVoucherListConfigRoute,
  integrationRoute,
  productAttributes,
  UploadProducts,
  TaskDetail,
  MufflerRoute,
  telecallLogs,
  CampaignAnalysisRoute,
  CustomerMigrateRoute,
  journeyBuilderSettingsRoute,
  channelWiseBlacklist,
  ...BlacklistConfigRoute,
  SchedulerListingRoute,
  BasketAnalysisRoute,
  ticketDesk,
  friendReferralSettingsRoute,
  friendReferralOtpRoute,
  executiveSummaryRoute,
  OverallJourneyAnalyticsRoutes,
  IndividualJourneyAnalyticsRoutes,
  ...HealthRoutes,
  ...SystemRoutes,
  ...ProductPreviewRoutes,
  ...JourneyRequestsRoutes,
  ...FeatureRequestsRoutes];

export const posRoutePages: RouteObject[] = [customer360POS];
const Routes: RouteObject[] = [
  InvoiceRoute,
  InvoiceV2Route,
  RaiseIssueInvoiceRoute,
  SurveyFormRoute,
  DLTFormRoute,
  {
    path: '/',
    element: <DashboardLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: "/",
        element: <PageGuard />,
        children: routesPages,
      },
    ],
  },
  {
    path: '/pos-dashboard/',
    element: <PosDashboard />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: '/pos-dashboard/',
        element: <PageGuard />,
        children: posRoutePages,
      },
    ],
  },
  {
    path: '/embed/',
    element: <SSOEmbedLayout />,
    errorElement: <ErrorBoundary />,
    children: [ssoEmbedLeadRoutes],
  },
  {
    path: '*',
    element: <Error404 />,
  },
];

export default Routes;
